import { CircularProgress, MenuItem } from '@material-ui/core'
import { Button } from 'components/facets'
import { getUserStockSummary, UserStockSummary } from 'lib/api'
import { useFetchUserPtInventory } from 'lib/hooks/useFetchUserPtInventory'
import { useInventoryStore } from 'lib/hooks/useInventoryStore'
import { FetchedStockInterface, useStockStore } from 'lib/hooks/useStockStore'
import { useSnackbar } from 'notistack'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { gameMetaData, IdGame, InventoryArticle, isSingle, STOCK_TYPES } from 'shared'
import { BACKOFFICE_USER_STOCK } from 'utils/constants'
import { InventoryPresenter } from '../stockPricing'
import { Filter } from '../stockPricing/FilterDock/Filter'
import { UserStockSummaryDialog } from './UserStockSummaryDialog'

export interface UserStockSearchProps {
  userId: string
}

const emptyStock = (): FetchedStockInterface => ({
  stock: [],
  sealedStock: [],
  fetchedAt: new Date().toISOString(),
})

// Generic is left out, as in the sidebar: every game's accessories already include the generic ones
const SELECTABLE_GAMES = gameMetaData.slice(1)

export const UserStockSearch = ({ userId }: UserStockSearchProps): JSX.Element => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { fetchUserPtInventory } = useFetchUserPtInventory()
  const zoomLevel = useInventoryStore((s) => s.zoomLevel)

  const [idGame, setIdGame] = useState<IdGame>(SELECTABLE_GAMES[0].idGame)
  const [loading, setLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [stockState, setStockState] = useState<FetchedStockInterface>(emptyStock)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [summary, setSummary] = useState<UserStockSummary | null>(null)
  const [setActiveStockType, setRefreshedStock] = useStockStore((s) => [
    s.setActiveStockType,
    s.setRefreshedStock,
  ])

  // Both lists are already loaded: switching only changes which one is shown. The refreshed flag
  // fetches nothing, it re-runs the step that puts the location on each card of the new list.
  const stockTypeActions = useMemo(
    () => [
      {
        key: 'singles-stock-btn',
        icon: 'fa-light fa-image',
        actionLabel: t('stockPricing.actions.singlesStock'),
        fn: () => {
          setActiveStockType(STOCK_TYPES.SINGLES)
          setRefreshedStock(true)
        },
      },
      {
        key: 'sealed-stock-btn',
        icon: 'fa-light fa-dice',
        actionLabel: t('stockPricing.actions.sealedStock'),
        fn: () => {
          setActiveStockType(STOCK_TYPES.SEALED)
          setRefreshedStock(true)
        },
      },
    ],
    [setActiveStockType, setRefreshedStock, t]
  )

  const loadStock = async (): Promise<void> => {
    // Without a user the endpoints answer with the logged-in account's own data: never ask them
    if (!userId) {
      enqueueSnackbar('select a user first', { variant: 'error' })
      return
    }
    setLoading(true)
    try {
      const { stock, maxProcessedAt } = await fetchUserPtInventory(idGame, zoomLevel, userId)

      const singles: InventoryArticle[] = []
      const sealed: InventoryArticle[] = []
      stock.forEach((article) => (isSingle(article.card) ? singles : sealed).push(article))

      setStockState({
        stock: singles,
        sealedStock: sealed,
        fetchedAt: maxProcessedAt ?? new Date().toISOString(),
      })
      setHasLoaded(true)
    } catch (error) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (error as Error)?.message
      enqueueSnackbar(message || t('error.generic'), { variant: 'error' })
    }
    setLoading(false)
  }

  const loadSummary = async (): Promise<void> => {
    setSummaryLoading(true)
    try {
      setSummary(await getUserStockSummary(userId))
    } catch (error) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (error as Error)?.message
      enqueueSnackbar(message || t('error.generic'), { variant: 'error' })
    }
    setSummaryLoading(false)
  }

  return (
    <div className="backoffice-user-stock mt-4">
      <div className="d-flex flex-column flex-sm-row align-items-sm-end gap-2">
        <div className="backoffice-user-stock-game">
          <Filter
            type="select"
            menuitems={SELECTABLE_GAMES.map((game) => (
              <MenuItem key={game.idGame} value={game.idGame} data-testid={`${game.name}-game`}>
                <span>{game.name}</span>
              </MenuItem>
            ))}
            setFilter={(_name, newValue) => setIdGame(newValue)}
            defaultValue={idGame}
            label={t('common.game')}
            name={'game'}
            activeFilter={false}
          />
        </div>
        <Button
          data-testid="load-user-stock-btn"
          variant="contained"
          onClick={loadStock}
          disabled={loading}
        >
          {loading ? <CircularProgress size={16} /> : t('backoffice.loadStock')}
        </Button>
        <Button
          data-testid="check-entire-inventory-btn"
          variant="outlined"
          onClick={loadSummary}
          disabled={!userId || summaryLoading}
        >
          {summaryLoading ? <CircularProgress size={16} /> : t('backoffice.checkEntireInventory')}
        </Button>
      </div>

      {hasLoaded && (
        <div className="backoffice-user-stock-panel mt-3" data-grid-viewport>
          <InventoryPresenter
            determineTab={() => 'articles'}
            emptyStockText={t('backoffice.userStockEmpty')}
            stockState={stockState}
            setStockState={setStockState}
            extraDockActions={stockTypeActions}
            viewOnly
            hideSettingsButton
            hideExportButton
            parentPage={BACKOFFICE_USER_STOCK}
          />
        </div>
      )}

      <UserStockSummaryDialog summary={summary} onClose={() => setSummary(null)} />
    </div>
  )
}
